import React, { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import Duration from "./images/duration.png";

export function UsersTable({ users, startDate, endDate }) {
  const [showIcon, setShowIcon] = useState(false);

  const columns = useMemo(
    () => [
      {
        accessorKey: "user_id",
        header: "User ID",
        cell: ({ row }) => row.getValue("user_id"),
        meta: {
          className: "col-user-id",
        },
      },
      {
        accessorKey: "mail_id",
        header: "Domain",
        cell: ({ row }) => (
          <div className="email-cell">{row.getValue("mail_id")}</div>
        ),
      },
      {
        accessorKey: "login_date",
        header: "Login Date",
        cell: ({ row }) => {
          const fullDate = row.getValue("login_date");
          return fullDate?.split(" ")[0];
        },
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("category")}</div>
        ),
      },
    ],
    []
  );

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: Array.isArray(users) ? users : [],
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Add global filter function
    globalFilterFn: (row, columnId, filterValue) => {
      const search = filterValue.toLowerCase();

      // Check each column you want to search
      const user_id = row.getValue("user_id").toString().toLowerCase();
      const mail_id = row.getValue("mail_id").toString().toLowerCase();
      const login_date = row.getValue("login_date").toString().toLowerCase();
      const category = row.getValue("category").toString().toLowerCase();

      return (
        user_id.includes(search) ||
        mail_id.includes(search) ||
        login_date.includes(search) ||
        category.includes(search)
      );
    },
  });
  useEffect(() => {
    if (!startDate || !endDate) {
      setShowIcon(false);
      return;
    }

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const isDefaultStart =
      startDate.getFullYear() === firstDayOfMonth.getFullYear() &&
      startDate.getMonth() === firstDayOfMonth.getMonth() &&
      startDate.getDate() === firstDayOfMonth.getDate();

    const isDefaultEnd =
      endDate.getFullYear() === today.getFullYear() &&
      endDate.getMonth() === today.getMonth() &&
      endDate.getDate() === today.getDate();

    setShowIcon(!(isDefaultStart && isDefaultEnd));
  }, [startDate, endDate]);
  if (!Array.isArray(users)) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="users-table-wrapper shadow p-4">
      {showIcon && (
        <img src={Duration} alt="duration" className="duration-icon" />
      )}
      <h3>SignedUp Users </h3>
      <div className="users-table-toolbar">
        <input
          type="text"
          placeholder="Search Users"
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="filter-input"
        />
      </div>

      {/* Rest of your table component remains the same */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="table-header-cell">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={row.getIsSelected() ? "row-selected" : ""}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`table-cell ${
                        cell.column.columnDef.meta?.className ?? ""
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={table.getAllLeafColumns()?.length ?? columns.length}
                  className="no-results"
                >
                  No results.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="users-table-footer">
        <div>{table.getFilteredRowModel().rows.length} user(s) shown</div>
        <div className="pagination-buttons">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className={`pagination-button next-button ${
              table.getCanPreviousPage() ? "highlight-next" : ""
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className={`pagination-button next-button ${
              table.getCanNextPage() ? "highlight-next" : ""
            }`}
          >
            Next
          </button>

        </div>
      </div>
    </div>
  );
}
